import { ForwardToInboxRounded, PersonOutlineOutlined } from '@mui/icons-material';
import {
    Box,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import Result from 'components/v2/common/Result';

import useTranslate from 'hooks/useTranslate';
import { Agent } from 'types';
import { getPlainText } from 'utils/dataFormatter';

interface Props {
    agents: Agent[];
    loading?: boolean;
    pendingInviteAgentId?: number | null;
    onResendInvitation: (agentId: number) => void;
}

const headCellSx = {
    py: 1.25,
    px: 1.5,
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const getFullName = (agent: Agent) =>
    [agent.firstname, agent.middlename, agent.lastname].filter(Boolean).join(' ') || `#${agent.id}`;

const OrganizationAgentsTable = ({
    agents,
    loading = false,
    pendingInviteAgentId = null,
    onResendInvitation,
}: Props) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const renderStatusCell = (agent: Agent) => {
        const isRegistered = (agent.status || '').toUpperCase() === 'REGISTERED';

        if (isRegistered) {
            return (
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        fontSize: 12,
                        fontWeight: 500,
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        backgroundColor: 'rgba(34, 197, 94, 0.12)',
                        color: '#15803d',
                    }}
                >
                    {translate('registered')}
                </Box>
            );
        }

        return (
            <Tooltip title={translate('resend')}>
                <span>
                    <IconButton
                        size="small"
                        disabled={pendingInviteAgentId === agent.id}
                        onClick={() => onResendInvitation(agent.id)}
                        sx={{
                            padding: '4px',
                            color: '#6d41d8',
                            '&:hover': {
                                backgroundColor: 'rgba(131, 83, 226, 0.08)',
                            },
                        }}
                    >
                        <ForwardToInboxRounded sx={{ fontSize: 18 }} />
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    if (isMobile) {
        if (loading) {
            return (
                <Stack spacing={1.5}>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.25}>
                                <Skeleton variant="text" width="55%" height={32} />
                                <Skeleton variant="text" width="72%" />
                                <Skeleton variant="text" width="48%" />
                                <Stack direction="row" spacing={1}>
                                    <Skeleton variant="rounded" width={92} height={24} />
                                    <Skeleton variant="rounded" width={84} height={24} />
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            );
        }

        if (!agents.length) {
            return (
                <Result
                    icon={<PersonOutlineOutlined sx={{ fontSize: 32 }} />}
                    title={translate('noAgentsYet')}
                    description={translate('noAgentsYetDescription')}
                />
            );
        }

        return (
            <Stack spacing={1.5}>
                {agents.map((agent) => (
                    <Paper key={agent.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={1.25}>
                            <Stack spacing={0.5}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {getFullName(agent)}
                                    </Typography>
                                    {renderStatusCell(agent)}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {agent.email || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('role')}: {getPlainText(agent.role) || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {agent.phone_number || '-'}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        );
    }

    if (!loading && !agents.length) {
        return (
            <Result
                icon={<PersonOutlineOutlined sx={{ fontSize: 32 }} />}
                title={translate('noAgentsYet')}
                description={translate('noAgentsYetDescription')}
            />
        );
    }

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 520, overflow: 'auto' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx }}>{translate('name')}</TableCell>
                            <TableCell sx={{ ...headCellSx }}>{translate('email')}</TableCell>
                            <TableCell sx={{ ...headCellSx }}>{translate('phone')}</TableCell>
                            <TableCell sx={{ ...headCellSx }}>{translate('role')}</TableCell>
                            <TableCell align="center" sx={{ ...headCellSx }}>
                                {translate('status')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? Array.from({ length: 6 }).map((_, index) => (
                                  <TableRow key={`loading-${index}`}>
                                      <TableCell>
                                          <Skeleton variant="text" width="60%" />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="text" width="75%" />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="text" width="55%" />
                                      </TableCell>
                                      <TableCell>
                                          <Skeleton variant="text" width="55%" />
                                      </TableCell>
                                      <TableCell align="center">
                                          <Skeleton
                                              variant="rounded"
                                              width={92}
                                              height={24}
                                              sx={{ mx: 'auto' }}
                                          />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : agents.map((agent) => (
                                  <TableRow
                                      key={agent.id}
                                      hover
                                      sx={{
                                          '&:nth-of-type(even)': {
                                              backgroundColor: 'rgba(148, 163, 184, 0.05)',
                                          },
                                          '& td': {
                                              borderBottomColor: 'rgba(148, 163, 184, 0.16)',
                                          },
                                      }}
                                  >
                                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                          {getFullName(agent)}
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                              {agent.email || '-'}
                                          </Typography>
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                              {agent.phone_number || '-'}
                                          </Typography>
                                      </TableCell>
                                      <TableCell>
                                          <Typography variant="body2" color="text.secondary">
                                              {getPlainText(agent.role) || '-'}
                                          </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                          {renderStatusCell(agent)}
                                      </TableCell>
                                  </TableRow>
                              ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default OrganizationAgentsTable;
